"use client"
import { useGSAP } from "@gsap/react";
import clsx from "clsx";
import gsap from "gsap";
import { useRef, useState } from "react";
import AnimatedLink from "@/components/animated-link";

import TopicCard from "@/components/card/topic-card/topic-card";
import Separator from "@/components/separator/separator";
import { ASSET_PREFIX, STORIES_BASE_PATH } from "@/constants/routes";
import { categories } from "@/data/static-data";
import Section from "../../section";

export default function RecordsNav({ className = "", isFull = false, hasTopBorder = true, currentCategory }) {
  const cats = isFull ? categories : categories.filter(c => c.slug !== currentCategory)
  const separator = useRef(null)
  const subtitle = useRef(null)
  const titles = useRef([])
  const titlesLines = useRef([])
  const cards = useRef([])

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(cards.current, {
        opacity: 1,
        y: 0
      })

      gsap.set(subtitle.current, {
        opacity: 1,
        y: 0
      })

      gsap.set(titles.current, {
        opacity: 1,
        y: 0
      })

      gsap.set(titlesLines.current, {
        scaleX: 1,
        transformOrigin: 'left center'
      })

      if (separator.current) {
        gsap.set(separator.current, {
          scaleX: 1,
          transformOrigin: 'left center'
        })
      }
    });

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(cards.current, {
        opacity: 0,
        y: 100
      })

      gsap.set(subtitle.current, {
        opacity: 0,
        y: 100
      })

      gsap.set(titles.current, {
        // opacity: 0,
        y: 100
      })

      gsap.set(titlesLines.current, {
        scaleX: 0,
        transformOrigin: 'left center'
      })

      if (separator.current) {
        gsap.set(separator.current, {
          scaleX: 0,
          transformOrigin: 'left center'
        })
      }
    });

    return () => mm.revert();
  })

  const animateIn = () => {
    gsap.killTweensOf([cards.current, separator.current, titles.current, subtitle.current]);

    if (separator.current) {
      gsap.to(separator.current, {
        scaleX: 1,
        duration: 1.2,
        ease: 'expo.out',
      })
    }

    gsap.to(subtitle.current, {
      opacity: 1,
      duration: 0.4,
      ease: 'linear.none',
    })

    gsap.to(subtitle.current, {
      y: 0,
      duration: 1.2,
      ease: 'expo.out',
    })

    gsap.to(cards.current, {
      opacity: 1,
      duration: 0.4,
      delay: 0.3,
      stagger: 0.1,
      ease: 'linear.none',
    })

    gsap.to(cards.current, {
      y: 0,
      delay: 0.3,
      stagger: 0.1,
      duration: 1.2,
      ease: 'expo.out',
    })

    gsap.to(titles.current, {
      y: 0,
      duration: 1.2,
      ease: 'expo.out',
      delay: 0.1,
      stagger: 0.1,
    })

    gsap.to(titlesLines.current, {
      scaleX: 1,
      duration: 1.2,
      ease: 'expo.out',
      delay: 0.1,
      stagger: 0.1,
    })
  }

  const animateOutBase = (dir) => {
    gsap.killTweensOf([cards.current, separator.current, titles.current, subtitle.current]);

    if (separator.current) {
      gsap.to(separator.current, {
        scaleX: 0,
        duration: 1.2,
        ease: 'expo.out',
      })
    }

    gsap.to(subtitle.current, {
      opacity: 0,
      duration: 0.4,
      ease: 'linear.none',
    })

    gsap.to(subtitle.current, {
      y: dir * 100,
      duration: 1.2,
      ease: 'expo.out',
    })

    gsap.to(cards.current, {
      opacity: 0,
      duration: 0.4,
      stagger: 0.1,
      ease: 'linear.none',
    })

    gsap.to(cards.current, {
      y: dir * 100,
      duration: 1.2,
      stagger: 0.1,
      ease: 'expo.out',
    })

    gsap.to(titles.current, {
      y: dir * 100,
      duration: 1.2,
      delay: 0.1,
      ease: 'expo.out',
      stagger: 0.1,
    })

    gsap.to(titlesLines.current, {
      scaleX: 0,
      duration: 1.2,
      delay: 0.1,
      ease: 'expo.out',
      stagger: 0.1,
    })
  }

  const animateOut = () => {
    animateOutBase(-1)
  }

  const animateOutBack = () => {
    animateOutBase(1)
  }

  const onEnter = () => { animateIn() }
  const onEnterBack = () => { animateIn() }
  const onLeave = () => { animateOut() }
  const onLeaveBack = () => { animateOutBack() }

  const [currentHoverIndex, setCurrentHoverIndex] = useState(-1)

  const handleLinkIn = (index) => {
    setCurrentHoverIndex(index)
  }

  const handleLinkOut = () => {
    setCurrentHoverIndex(-1)
  }

  return (
    <Section theme="main-dark" className="py-(--space-11) lg:py-26 flex justify-center relative" onEnter={onEnter} onEnterBack={onEnterBack} onLeave={onLeave} onLeaveBack={onLeaveBack}>
      <img alt="" src={`${ASSET_PREFIX}/img/svg/topography-bg.svg`} className="absolute top-0 left-0 w-full h-full object-cover pointer-events-none" />
      {hasTopBorder && <Separator className="!absolute top-0 record-nav-content-width" ref={separator} />}
      <div className="record-nav-content-width flex max-lg:flex-col gap-(--space-7) lg:gap-(--space-9) items-start relative">
        <div className="flex flex-col justify-around grow-1 h-full max-lg:w-full">
          <span className="body-1-small xl:body-1 mb-(--space-9) lg:mb-auto xl:mt-0 will-change-transform" ref={subtitle}>See other categories</span>
          <ul className="flex flex-col">
            {cats.map((cat, index) => {
              const isLast = index === cats.length - 1;
              return (
                <li
                  key={cat.slug}
                  className={clsx(
                    "pb-(--space-5) lg:pb-(--space-6) xl:pb-(--space-5) relative overflow-y-clip t-opacity",
                    index < cats.length - 1 && 'mb-(--space-5) lg:mb-(--space-5) mb-(--space-5)',
                    currentHoverIndex >= 0 && currentHoverIndex !== index && 'opacity-50'
                  )}
                >
                  <AnimatedLink
                    href={`${STORIES_BASE_PATH}/record-gallery/${cat.slug === 'featured' ? '' : cat.slug}`}
                    onPointerEnter={() => handleLinkIn(index)}
                    onPointerLeave={handleLinkOut}
                    onFocus={() => handleLinkIn(index)}
                    onBlur={handleLinkOut}
                    className="block -outline-offset-2"
                  >
                    <h3 className="relative -top-px subnav-small lg:heading-5 xl:heading-4 will-change-transform whitespace-nowrap" ref={(el) => titles.current[index] = el}>{cat.title}</h3>
                    <div className={clsx("absolute bottom-0 left-0 w-full h-[1px] border-b border-dashed  border-current/20 will-change-transform", isLast && "max-lg:hidden")} ref={(el) => titlesLines.current[index] = el} />
                  </AnimatedLink>
                </li>
              )
            })}
          </ul>
        </div>
        <TopicCard href={`${STORIES_BASE_PATH}/250-stories`} label="" title="250 Stories" subtitle="Explore personal memories and photos." className="bg-mint-green max-lg:grow-1 max-lg:shrink-0 will-change-transform" subtitleClassName="w-[50%] lg:w-[100%] xl:w-[80%]" ref={(el) => cards.current[0] = el}>
          <img src={`${ASSET_PREFIX}/img/polaroid.png`} alt="polaroid" width="170" height="213" className="w-auto max-lg:h-[78%] lg:w-[9.625rem] lg-landscape:w-[10.625rem] xl:w-[11.625rem] max-lg:rotate-[-4deg] lg:h-auto absolute bottom-(--space-5) lg:bottom-(--space-3) max-lg:-right-(--space-5) lg:left-(--space-5) z-0 lg:z-10" />
        </TopicCard>
        <TopicCard href={`${STORIES_BASE_PATH}/map`}label="" title="Map view" subtitle="See stories across the 50 states." className="bg-beige-old max-lg:grow-1 max-lg:shrink-0 will-change-transform" subtitleClassName="w-[40%] lg:w-[80%] xl:w-[70%]" ref={(el) => cards.current[1] = el}>
          <picture className="w-auto h-full lg:w-[150%] xl:w-full lg:h-auto absolute bottom-0 right-0 xl:right-initial xl:left-0 z-0">
            <source media="(max-width: 1439px)" srcSet={`${ASSET_PREFIX}/img/map-card-mobile.png`} />
            <source media="(min-width: 1440px)" srcSet={`${ASSET_PREFIX}/img/map-card.png`} />
            <img src={`${ASSET_PREFIX}/img/map-card.png`} alt="map" width="304" height="437" className="w-auto h-full lg:w-full lg:h-auto" />
          </picture>
        </TopicCard>
      </div>
    </Section>
  )
}
